import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  CheckCircle2,
  Loader2,
  Users,
} from "lucide-react";
import { track } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "ggg.chat.session";
const HISTORY_KEY = "ggg.chat.history";
const GREETED_KEY = "ggg.chat.greeted";
const AUTO_OPEN_DELAY_MS = 4500;

function getOrCreateSession() {
  try {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid =
        "s-" +
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 8);
      localStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    return "s-" + Date.now().toString(36);
  }
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(messages) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-50)));
  } catch {
    /* ignore */
  }
}

const WELCOME = {
  role: "assistant",
  content:
    "Hey there — I'm GAGE, the Gutter Assistant for Guidance & Estimates. Ask me anything about gutter cleaning, covers, repairs, or window screens. I can also help you get on Mike's schedule.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const h = loadHistory();
    return h.length ? h : [WELCOME];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getOrCreateSession());

  useEffect(() => {
    saveHistory(messages);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, booking]);

  // Auto-greet on first visit — pop the chat open after a few seconds
  useEffect(() => {
    let greeted = false;
    try {
      greeted = localStorage.getItem(GREETED_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (greeted) return;
    const t = setTimeout(() => {
      setOpen(true);
      track("chat_opened", { trigger: "auto" });
      try {
        localStorage.setItem(GREETED_KEY, "1");
      } catch {
        /* ignore */
      }
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: trimmed,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply || "..." },
      ]);
      if (data.booking) {
        setBooking(data.booking);
        setBookingDone(false);
        track("quote_generated_by_gage", {
          service_type: data.booking.service_type || "unknown",
          total: data.booking.total ?? data.booking.price ?? null,
        });
      }
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Hmm, I'm having trouble right now. Best bet is to text \"Gutters\" to (712) 310-9514 and Mike will get back to you.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    if (!booking || bookingSubmitting) return;
    setBookingSubmitting(true);
    try {
      const payload = { ...booking };
      if (!payload.email) delete payload.email;
      if (!payload.message) payload.message = "Booked via chat assistant.";
      await axios.post(`${API}/contact`, payload);
      setBookingDone(true);
      toast.success("Booking sent — Mike will reach out shortly.");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Got it — booking sent through. Mike will text or call you to confirm. Thanks!",
        },
      ]);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send the booking. Please text (712) 310-9514.");
    } finally {
      setBookingSubmitting(false);
    }
  };

  const resetChat = () => {
    setMessages([WELCOME]);
    setBooking(null);
    setBookingDone(false);
    try {
      localStorage.removeItem(HISTORY_KEY);
      // keep session id so backend retains continuity
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => {
          setOpen((o) => !o);
          if (!open) track("chat_opened", { trigger: "manual" });
          try {
            localStorage.setItem(GREETED_KEY, "1");
          } catch {
            /* ignore */
          }
        }}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-[#5FBE1F] text-black grid place-items-center shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:bg-[#4A9818] hover:text-white transition-colors"
        data-testid="chat-launcher"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-5 z-[60] w-[min(94vw,380px)] h-[min(80vh,580px)] bg-white rounded-md shadow-[0_24px_60px_rgba(0,0,0,0.28)] border border-[#E5E5E5] overflow-hidden flex flex-col"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-[#0A0A0A] text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-[#5FBE1F] grid place-items-center text-black font-display text-lg">
                  G
                </div>
                <div>
                  <div className="font-display text-base leading-none">
                    Glenwood Gutter Guy
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA09B] mt-1">
                    Usually replies in seconds
                  </div>
                </div>
              </div>
              <button
                onClick={resetChat}
                className="text-[10px] uppercase tracking-[0.18em] text-[#9CA09B] hover:text-white"
                data-testid="chat-reset"
              >
                Reset
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#F5F5F5]"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed rounded-md whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#0A0A0A] text-white rounded-br-none"
                        : "bg-white border border-[#E5E5E5] text-[#000] rounded-bl-none"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#E5E5E5] rounded-md rounded-bl-none px-3 py-2 text-sm text-[#4A4A46] inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Typing…
                  </div>
                </div>
              )}

              {/* Suggestion chips — show only on a fresh conversation */}
              {!loading &&
                messages.length === 1 &&
                messages[0].role === "assistant" && (
                  <div
                    className="flex flex-wrap gap-2 pt-1"
                    data-testid="chat-suggestion-chips"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        send(
                          "I'd like the Neighbor Special — 2 homes on my block for $150 total. Help me set it up for both addresses."
                        )
                      }
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#5FBE1F] text-[#000] px-3 py-2 rounded-full hover:bg-[#5FBE1F] hover:text-black transition-colors"
                      data-testid="chip-neighbor-special"
                    >
                      <Users className="w-3.5 h-3.5 text-[#5FBE1F]" />
                      I have a neighbor interested — 2 for $150
                    </button>
                  </div>
                )}

              {/* Booking confirmation card */}
              {booking && !bookingDone && (
                <div className="bg-white border-2 border-[#5FBE1F] rounded-md p-3" data-testid="chat-booking-card">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-2">
                    Confirm Booking
                  </div>
                  <dl className="text-xs text-[#000] grid grid-cols-[80px_1fr] gap-y-1 gap-x-2">
                    <dt className="text-[#4A4A46]">Name</dt><dd>{booking.name}</dd>
                    <dt className="text-[#4A4A46]">Phone</dt><dd>{booking.phone}</dd>
                    <dt className="text-[#4A4A46]">Address</dt><dd>{booking.address}</dd>
                    <dt className="text-[#4A4A46]">Service</dt><dd>{booking.service_type}</dd>
                    {booking.preferred_date && (
                      <>
                        <dt className="text-[#4A4A46]">When</dt>
                        <dd>{booking.preferred_date} · {booking.preferred_time_window || "flexible"}</dd>
                      </>
                    )}
                    {booking.message && (
                      <>
                        <dt className="text-[#4A4A46]">Notes</dt>
                        <dd>{booking.message}</dd>
                      </>
                    )}
                  </dl>
                  <button
                    onClick={submitBooking}
                    disabled={bookingSubmitting}
                    className="mt-3 w-full bg-[#5FBE1F] text-black font-bold py-2 rounded-sm hover:bg-[#4A9818] hover:text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    data-testid="chat-booking-submit"
                  >
                    {bookingSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                    ) : (
                      <>Send to Mike <Send className="w-3.5 h-3.5" /></>
                    )}
                  </button>
                </div>
              )}
              {booking && bookingDone && (
                <div className="bg-[#0A0A0A] text-white rounded-md p-3 inline-flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#5FBE1F]" />
                  Sent. Mike will text or call shortly.
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-[#E5E5E5] bg-white p-3 flex items-end gap-2"
            >
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Type a message…"
                disabled={loading}
                className="flex-1 px-3 py-2 text-sm bg-[#F5F5F5] border border-[#E5E5E5] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#5FBE1F] resize-none leading-relaxed min-h-[40px] max-h-[140px] overflow-y-auto"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="h-10 px-4 bg-[#5FBE1F] text-black font-bold rounded-sm hover:bg-[#4A9818] hover:text-white transition-colors disabled:opacity-50 flex-shrink-0"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
